import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FtthResourceService, Resource, ResSpecificationId } from '../../../../../services/api/ftth-resource/ftth-resource.service';
import { Subscription } from 'rxjs';
import { NotificationLevel, NotificationsService } from '../../../../../services/api/notifications/notifications.service';
import { TranslateService } from '@ngx-translate/core';
import { delay, map, retry, retryWhen, switchMap, take, tap } from 'rxjs/operators';
import { AppService } from '../../../../../app.service';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import saveAs from 'file-saver';

@Component({
  selector: 'app-administration-tab',
  templateUrl: './administration-tab.component.html',
  styleUrls: ['./administration-tab.component.scss']
})
export class AdministrationTabComponent implements OnInit, OnDestroy {

  @Input()
  resource: Resource;

  namingId: string;

  @Output()
  reloadResource: EventEmitter<void>;

  exportSuccess: boolean;
  filename: string;
  appData: any;

  olts: any[];
  oltSlots: any[];
  oltPorts: any[];

  searchGroup: FormGroup;

  private subscriptions = new Subscription();

  constructor(
    private ftthResourceService: FtthResourceService,
    private notificationsService: NotificationsService,
    private translateService: TranslateService,
    private appService: AppService,
    private formBuilder: FormBuilder
  ) {
    this.reloadResource = new EventEmitter<void>();
  }

  ngOnInit() {
    this.exportSuccess = false;
    this.appData = this.appService.settings;

    this.namingId = this.resource._links.self.href.split('/')[this.resource._links.self.href.split('/').length - 1];

    if (this.resource.specificationName === 'CTOResSpec') {
      this.searchGroup = this.formBuilder.group({
        olt: this.formBuilder.control(''),
        oltSlot: this.formBuilder.control(''),
        oltPort: this.formBuilder.control(''),
      });
      this.getOLTCombos();
    }

  }

  getOLTCombos() {
    this.olts = [];
    this.ftthResourceService.getResources('OLTResSpec', '', null, 0, 1000, null, null, true).subscribe(
      (data) => {
        for (const olt of data.resources) {
          if (olt.lifecycleState === 'FREE') {
            this.olts.push({ name: olt.name.replace(/OLT-/g, ''), value: olt.name.replace(/OLT-/g, '') });
          }
        }
        if (this.olts.length > 0) {
          this.oltControl.setValue(this.olts[0].value);
        }
        this.getOLTSlots();
      },
    );
  }


  getOLTSlots() {
    this.oltSlots = [];
    this.ftthResourceService.getResources('OLTCardResSpec', 'name', this.oltControl.value + '-', 0, 1000, null, null, true)
      .subscribe(
        (data) => {
          for (const oltSlot of data.resources) {
            if (oltSlot.lifecycleState === 'FREE') {
              this.oltSlots.push({ name: oltSlot.name.split('-').pop(), value: oltSlot.name.replace(/OLTCard-/g, '') });
            }
          }
          if (this.oltSlots.length > 0) {
            this.oltSlotControl.setValue(this.oltSlots[0].value);
          }
          this.getOLTPorts();
        },
      );
  }

  getOLTPorts() {
    this.oltPorts = [];
    this.ftthResourceService.getResources('OLTPortResSpec', 'name', this.oltSlotControl.value + '-', 0, 1000, null, null, true)
      .subscribe(
        (data) => {
          for (const oltPort of data.resources) {
            if (oltPort.lifecycleState === 'FREE') {
              this.oltPorts.push({ name: oltPort.name.split('-').pop(), value: oltPort.name.replace(/OLTPort-/g, '') });
            }
          }
          if (this.oltPorts.length > 0) {
            this.oltPortControl.setValue(this.oltPorts[0].value);
          }
        },
      );
  }


  get oltControl(): FormControl {
    return this.searchGroup.get('olt') as FormControl;
  }

  get oltSlotControl(): FormControl {
    return this.searchGroup.get('oltSlot') as FormControl;
  }

  get oltPortControl(): FormControl {
    return this.searchGroup.get('oltPort') as FormControl;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  moveCTO() {
    this.subscriptions.add(this.ftthResourceService.moveCTO(this.namingId, 'OLTPort-' + this.oltPortControl.value)
      .subscribe(
        () => {
          this.reloadResource.emit();
          this.notificationsService.showNotification(
            this.translateService.instant('FTTH_RESOURCE.MOVE_SUCCESS'),
            NotificationLevel.SUCCESS);
        },
        () => {
          this.notificationsService.showNotification(
            this.translateService.instant('FTTH_RESOURCE.MOVE_ERROR'),
            NotificationLevel.ERROR);
        }
      ));
  }

  enableResource() {
    this.subscriptions.add(this.ftthResourceService.enable(this.resource.name)
      .subscribe(
        () => {
          this.reloadResource.emit();
          this.notificationsService.showNotification(
            this.translateService.instant('FTTH_RESOURCE.ENABLE_SUCCESS'),
            NotificationLevel.SUCCESS);
        },
        () => {
          this.notificationsService.showNotification(
            this.translateService.instant('FTTH_RESOURCE.ENABLE_ERROR'),
            NotificationLevel.ERROR);
        }
      ));
  }

  disableResource() {
    this.subscriptions.add(this.ftthResourceService.disable(this.resource.name)
      .subscribe(
        () => {
          this.reloadResource.emit();
          this.notificationsService.showNotification(
            this.translateService.instant('FTTH_RESOURCE.DISABLE_SUCCESS'),
            NotificationLevel.SUCCESS);
        },
        () => {
          this.notificationsService.showNotification(
            this.translateService.instant('FTTH_RESOURCE.DISABLE_ERROR'),
            NotificationLevel.ERROR);
        }
      ));
  }

  hasExportNetwork() {
    const exportNetworkTabTypes: ResSpecificationId[] = ['OLTCardResSpec', 'OLTPortResSpec', 'CTOResSpec', 'SplitterResSpec', 'CTOPortResSpec'];
    return exportNetworkTabTypes.includes(this.resource.specificationName);
  }

  exportResource() {
    this.subscriptions.add(
      this.ftthResourceService.generateExportFile(this.namingId)
        .pipe(
          switchMap(filename => this.ftthResourceService.exportFileIsOpen(filename)
            .pipe(
              map(state => {
                if (state !== 'EXPORT_FINISHED') {
                  throw state;
                }
                return filename;
              }),
              retryWhen(error => error.pipe(delay(1000), take(4)))
            ))
        )
        .subscribe(filename => {
          this.exportSuccess = true;
          this.filename = filename;
        })
    );
  }

  downloadFile() {
    this.subscriptions.add(
      this.ftthResourceService.downloadFile(this.filename).subscribe({
        next: (resp: ArrayBuffer) => {
          const blob = new Blob([resp], { type: 'application/octet-stream' });
          saveAs(blob, this.filename);
          this.exportSuccess = false;
          this.filename = undefined;
        },
        error: error => console.error(error)
      })
    );
  }

  preselect() {
    this.subscriptions.add(this.ftthResourceService.preselect(this.resource.name)
      .subscribe(
        () => {
          this.reloadResource.emit();
          this.notificationsService.showNotification(
            this.translateService.instant('FTTH_RESOURCE.PRESELECT_SUCCESS'),
            NotificationLevel.SUCCESS);
        },
        () => {
          this.notificationsService.showNotification(
            this.translateService.instant('FTTH_RESOURCE.PRESELECT_ERROR'),
            NotificationLevel.ERROR);
        }
      ));
  }

  deselect() {
    this.subscriptions.add(this.ftthResourceService.deselect(this.resource.name)
      .subscribe(
        () => {
          this.reloadResource.emit();
          this.notificationsService.showNotification(
            this.translateService.instant('FTTH_RESOURCE.DESELECT_SUCCESS'),
            NotificationLevel.SUCCESS);
        },
        () => {
          this.notificationsService.showNotification(
            this.translateService.instant('FTTH_RESOURCE.DESELECT_ERROR'),
            NotificationLevel.ERROR);
        }
      ));
  }
}
